/**
 * Contrato entre la API y el panel. Todo lo que cruza la red vive aquí, en
 * camelCase; la base de datos usa snake_case y cada módulo traduce al salir.
 */

export type Rol = 'SUPER_ADMIN' | 'ADMIN' | 'MIEMBRO';
export type TipoNodo = 'CARPETA' | 'ARCHIVO';
export type SujetoTipo = 'USUARIO' | 'GRUPO' | 'TODOS';

/** Las cuatro capacidades sobre un nodo. Se otorgan, nunca se deniegan. */
export interface Permisos {
  /** Ver la carpeta/archivo, listar su contenido, descargar. */
  leer: boolean;
  /** Crear dentro, subir, renombrar, mover hacia adentro, nuevas versiones. */
  escribir: boolean;
  /** Enviar a papelera y purgar. */
  eliminar: boolean;
  /** Generar enlaces que dan acceso sin cuenta a este elemento. */
  compartir: boolean;
  /** Cambiar los permisos del nodo y romper/restaurar la herencia. */
  gestionar: boolean;
}

export const SIN_PERMISOS: Permisos = {
  leer: false, escribir: false, eliminar: false, compartir: false, gestionar: false,
};
export const TODOS_LOS_PERMISOS: Permisos = {
  leer: true, escribir: true, eliminar: true, compartir: true, gestionar: true,
};

export interface UsuarioSesion {
  id: string;
  email: string;
  nombre: string;
  rol: Rol;
  activo: boolean;
  organizacionId: string;
  organizacionNombre: string;
  /** Carpeta raíz de la organización: el punto de entrada del explorador. */
  raizId: string;
  grupos: Array<{ id: string; nombre: string }>;
}

export interface Nodo {
  id: string;
  padreId: string | null;
  tipo: TipoNodo;
  nombre: string;
  mime: string | null;
  bytes: number | null;
  version: number;
  extension: string | null;
  heredaPermisos: boolean;
  creadoPor: string | null;
  creadoPorNombre: string | null;
  creadoEn: string;
  actualizadoEn: string;
  eliminadoEn: string | null;
  /** Permisos efectivos del usuario que hace la petición sobre ESTE nodo. */
  permisos: Permisos;
  /** Solo en carpetas: cuántos hijos directos tiene (sin contar la papelera). */
  hijos?: number;
}

export interface Miga {
  id: string;
  nombre: string;
}

export interface ListadoCarpeta {
  carpeta: Nodo;
  ruta: Miga[];
  hijos: Nodo[];
}

export interface NodoArbol {
  id: string;
  nombre: string;
  padreId: string | null;
  tieneHijos: boolean;
  heredaPermisos: boolean;
  permisos: Permisos;
}

export interface VersionArchivo {
  id: string;
  version: number;
  bytes: number;
  mime: string;
  sha256: string;
  comentario: string | null;
  creadoPor: string | null;
  creadoPorNombre: string | null;
  creadoEn: string;
  esActual: boolean;
}

export interface EntradaPermiso {
  id: string;
  sujetoTipo: SujetoTipo;
  sujetoId: string | null;
  sujetoNombre: string;
  sujetoDetalle: string | null;
  permisos: Permisos;
  /** El nodo donde está escrita la entrada (puede ser un ancestro). */
  origenId: string;
  origenNombre: string;
  heredada: boolean;
}

export interface PermisosDeNodo {
  nodo: { id: string; nombre: string; tipo: TipoNodo; heredaPermisos: boolean };
  entradas: EntradaPermiso[];
}

export interface UsuarioAdmin {
  id: string;
  email: string;
  nombre: string;
  rol: Rol;
  activo: boolean;
  organizacionId: string;
  organizacionNombre: string;
  creadoEn: string;
  grupos: Array<{ id: string; nombre: string }>;
}

export interface Grupo {
  id: string;
  nombre: string;
  descripcion: string | null;
  miembros: number;
}

export interface GrupoDetalle extends Grupo {
  usuarios: Array<{ id: string; nombre: string; email: string }>;
}

export interface EntradaBitacora {
  id: string;
  accion: string;
  usuarioId: string | null;
  usuarioNombre: string | null;
  nodoId: string | null;
  nodoRuta: string | null;
  detalle: Record<string, unknown> | null;
  ip: string | null;
  creadoEn: string;
}

export interface ResultadoBusqueda extends Nodo {
  ruta: string;
}
